import api from '@/libs/axiosInstance';
import type { ApiResponse, ListMentionBoostCampaignsResponse } from '@fluenceur/types';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

const MENTION_BOOST_CAMPAIGNS_QUERY_KEY_PREFIX = 'mentionBoostCampaigns';

export const mentionBoostCampaignsQueryKey = (
  mentionId: string | null | undefined,
  adAccountId: string | null | undefined,
  search: string | null | undefined
) => [MENTION_BOOST_CAMPAIGNS_QUERY_KEY_PREFIX, mentionId, adAccountId ?? null, search ?? null];

const fetchMentionBoostCampaigns = async (
  mentionId: string,
  adAccountId: string | null,
  search: string | null
): Promise<ListMentionBoostCampaignsResponse> => {
  const response = await api.get<ApiResponse<ListMentionBoostCampaignsResponse>>(
    `/dashboard/mentions/${mentionId}/boost-campaigns`,
    {
      params: {
        ...(adAccountId ? { adAccountId } : {}),
        ...(search ? { search } : {}),
      },
    }
  );

  if (!response.data.data) {
    throw new Error(response.data.message || 'Boost campaigns are unavailable.');
  }

  return response.data.data;
};

interface UseMentionBoostCampaignsQueryOptions {
  mentionId: string | null | undefined;
  // The ad account the campaigns belong to; the server falls back to the
  // brand's default when null.
  adAccountId?: string | null;
  // Name fragment, applied provider-side so a large account can find a campaign
  // that a truncated listing would not contain.
  search?: string | null;
  enabled?: boolean;
}

/**
 * The brand's campaigns that can host this boost. Short staleTime: a brand that
 * just created a campaign in Ads Manager expects to find it here on reopening
 * the placement section. Only fetched when the section is actually opened.
 */
export const useMentionBoostCampaignsQuery = ({
  mentionId,
  adAccountId = null,
  search = null,
  enabled = true,
}: UseMentionBoostCampaignsQueryOptions): UseQueryResult<
  ListMentionBoostCampaignsResponse,
  Error
> => {
  return useQuery<ListMentionBoostCampaignsResponse, Error>({
    queryKey: mentionBoostCampaignsQueryKey(mentionId, adAccountId, search),
    queryFn: () => {
      if (!mentionId) {
        return Promise.reject(new Error('Mention ID is required.'));
      }
      return fetchMentionBoostCampaigns(mentionId, adAccountId, search);
    },
    // Keep the previous page visible while a new search resolves, so the list
    // does not blink to empty on every keystroke.
    placeholderData: (previous) => previous,
    enabled: !!mentionId && enabled,
    staleTime: 15 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 1,
  });
};

export { fetchMentionBoostCampaigns };
